/* ==========================================================================
   Webbe · shared behaviour for custom sections (Horizon 4.x)
   Loaded once from layout/theme.liquid as type="module".
   ========================================================================== */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Reveal on scroll ---------- */
const pending = new Set();
const revealIO = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) show(e.target);
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 })
  : null;
function show(n) {
  n.classList.add('is-in'); pending.delete(n); revealIO && revealIO.unobserve(n);
  // after the transition, clear clip-path/will-change so absolutely positioned children can overflow
  const done = () => n.classList.add('is-done');
  const d = parseFloat(getComputedStyle(n).transitionDuration) || 0;
  if (d) setTimeout(done, d * 1000 + 400); else done();
}
function bindReveals(root = document) {
  root.querySelectorAll('[data-reveal]:not(.is-in)').forEach((n) => {
    const par = n.parentElement;
    if (par && par.classList.contains('w-stagger') && !n.style.getPropertyValue('--i')) n.style.setProperty('--i', String(Array.from(par.children).indexOf(n)));
    const sec = n.closest('[data-anim]');
    if (REDUCED.matches || !revealIO || (sec && sec.dataset.anim === 'none')) { n.classList.add('is-in'); return; }
    pending.add(n); revealIO.observe(n);
  });
  doSweep(); startInterval();
}
/* Safety net: anything already at or above the viewport gets shown, so a fast scroll can never leave a section invisible.
   Runs on scroll (debounced), and on a light interval that stops itself once nothing is pending. */
function doSweep() {
  if (!pending.size) return;
  const vh = window.innerHeight;
  for (const n of Array.from(pending)) { const r = n.getBoundingClientRect(); if (r.top < vh * 0.92 || r.bottom < 0) show(n); }
}
let sweepT = 0, sweepI = 0;
function sweep() { clearTimeout(sweepT); sweepT = setTimeout(doSweep, 90); }
function startInterval() { if (sweepI) return; sweepI = setInterval(() => { doSweep(); if (!pending.size) { clearInterval(sweepI); sweepI = 0; } }, 400); }
window.addEventListener('scroll', sweep, { passive: true });
window.addEventListener('resize', sweep);
window.addEventListener('load', doSweep);
bindReveals();
document.addEventListener('shopify:section:load', (e) => bindReveals(e.target));


/* ---------- anchor navigation: measured offset + smooth scroll ---------- */
function headerEl() { return document.querySelector('#header-component, .w-header, .wh'); }

function measureHeader() {
  const h = headerEl();
  if (h) document.documentElement.style.setProperty('--w-header-h', Math.round(h.getBoundingClientRect().height) + 'px');
}
measureHeader();
window.addEventListener('load', measureHeader);
window.addEventListener('resize', measureHeader);
document.addEventListener('shopify:section:load', measureHeader);
if ('ResizeObserver' in window) { const h = headerEl(); if (h) new ResizeObserver(measureHeader).observe(h); }

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

function anchorOffset() {
  const cs = getComputedStyle(document.documentElement);
  const h = parseFloat(cs.getPropertyValue('--w-header-h')) || 80;
  const gap = parseFloat(cs.getPropertyValue('--w-anchor-gap')) || 16;
  return h + gap;
}

// Find whatever actually scrolls. Horizon and the theme editor preview can put
// overflow:hidden on html/body and scroll an inner wrapper instead.
let _scrollRoot = null;
function scrollRoot(from) {
  if (_scrollRoot && _scrollRoot.isConnected) return _scrollRoot;
  const isScroller = (n) => {
    if (!n || n === document) return false;
    const cs = getComputedStyle(n);
    return /(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 4;
  };
  let n = from ? from.parentElement : null;
  while (n && n !== document.body && n !== document.documentElement) { if (isScroller(n)) return (_scrollRoot = n); n = n.parentElement; }
  const se = document.scrollingElement || document.documentElement;
  if (se.scrollHeight > se.clientHeight + 4 && !/hidden/.test(getComputedStyle(se).overflowY)) return (_scrollRoot = se);
  // last resort: a scrollable wrapper near the top of the tree (checked once, then cached)
  let best = null, bestH = 0;
  document.querySelectorAll('body > *, body > * > *, body > * > * > *').forEach((el) => { if (isScroller(el) && el.scrollHeight > bestH) { best = el; bestH = el.scrollHeight; } });
  return (_scrollRoot = best || se);
}
window.addEventListener('resize', () => { _scrollRoot = null; });
// warm the cache as early as possible so the first click has nothing to look up
const warmRoot = () => { const s = document.querySelector('main section, .w-section'); if (s) scrollRoot(s); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', warmRoot); else warmRoot();
window.addEventListener('load', warmRoot);

/* ---------- Smooth scroll (Lenis) ----------
   Settings: Theme settings → Webbe · Smooth scroll (window.webbeSmooth, set in webbe-theme-vars).
   Desktop scrolls .page-wrapper, mobile scrolls the window, so Lenis is rebuilt when the layout crosses 990px. */
let lenis = null;
const isDocRoot = (r) => r === document.scrollingElement || r === document.documentElement;
function initSmooth() {
  const cfg = window.webbeSmooth;
  if (lenis) { lenis.destroy(); lenis = null; window.webbeLenis = null; }
  if (!cfg || !window.Lenis || REDUCED.matches || (window.Shopify && window.Shopify.designMode)) return;
  _scrollRoot = null;
  const root = scrollRoot(document.querySelector('main .w-section, main section, main') || document.body);
  const docRoot = isDocRoot(root);
  lenis = new window.Lenis({
    wrapper: docRoot ? window : root,
    content: docRoot ? document.documentElement : (root.querySelector('main') || root.firstElementChild || root),
    lerp: cfg.lerp || 0.12,
    wheelMultiplier: cfg.wheel || 1,
    smoothWheel: true,
    syncTouch: !!cfg.touch,
    autoRaf: true,
    // let drawers, dialogs and the lightbox scroll on their own
    prevent: (node) => !!(node && node.closest && node.closest('[data-lenis-prevent], dialog, [role="dialog"], .w-header__drawer, .w-lb, cart-drawer-component, .drawer, .menu-drawer, predictive-search-component')),
  });
  window.webbeLenis = lenis;
  syncLock();
}
// pause while the theme or the Webbe header locks scrolling (menus, drawers, modals)
function syncLock() {
  if (!lenis) return;
  const h = document.documentElement;
  if (h.hasAttribute('scroll-lock') || h.classList.contains('w-scroll-lock')) lenis.stop(); else lenis.start();
}
new MutationObserver(syncLock).observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'scroll-lock'] });
const startSmooth = () => { if (window.Lenis) initSmooth(); else window.addEventListener('load', initSmooth, { once: true }); };
if (window.webbeSmooth) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startSmooth); else startSmooth();
  window.matchMedia('(min-width: 990px)').addEventListener('change', () => initSmooth());
  REDUCED.addEventListener && REDUCED.addEventListener('change', () => initSmooth());
}

const readY = (r) => (r === document.scrollingElement || r === document.documentElement) ? (window.scrollY || r.scrollTop) : r.scrollTop;
const writeY = (r, y) => { if (r === document.scrollingElement || r === document.documentElement) window.scrollTo(0, y); else r.scrollTop = y; };
const viewH = (r) => (r === document.scrollingElement || r === document.documentElement) ? window.innerHeight : r.clientHeight;

let scrollRaf = 0;
function scrollToEl(el, opts = {}) {
  if (!el) return;
  cancelAnimationFrame(scrollRaf);
  const root = scrollRoot(el);

  // target recomputed each frame: content above can shift while we travel
  const target = () => {
    const max = Math.max(0, root.scrollHeight - viewH(root));
    const rootTop = (root === document.scrollingElement || root === document.documentElement) ? 0 : root.getBoundingClientRect().top;
    const y = el.getBoundingClientRect().top - rootTop + readY(root) - anchorOffset();
    return Math.max(0, Math.min(max, y));
  };

  const from = readY(root);
  if (Math.abs(target() - from) < 2) return;

  // with smooth scroll on, let Lenis drive the motion so the two never fight
  if (lenis) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || opts.instant) {
      lenis.scrollTo(target(), { immediate: true, force: true });
      settle(root, target);
      return;
    }
    const d = Math.min(1200, Math.max(600, Math.abs(target() - from) * 0.42));
    lenis.scrollTo(target(), { duration: d / 1000, easing: easeOutCubic, force: true, onComplete: () => settle(root, target) });
    return;
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || opts.instant) {
    writeY(root, target());
    setTimeout(() => writeY(root, target()), 120);
    return;
  }

  const dur = Math.min(1200, Math.max(600, Math.abs(target() - from) * 0.42));
  const t0 = performance.now();
  let done = false, tgt = target(), lastMeasure = t0;
  const finish = () => { if (done) return; done = true; cancelAnimationFrame(scrollRaf); clearTimeout(backstop); writeY(root, target()); settle(root, target); };
  const step = (now) => {
    if (done) return;
    // re-measure the target ~12x per second rather than every frame: enough to follow layout shifts, cheap enough to stay smooth
    if (now - lastMeasure > 80) { tgt = target(); lastMeasure = now; }
    const p = Math.min(1, (now - t0) / dur);
    writeY(root, from + (tgt - from) * easeOutCubic(p));
    if (p < 1) scrollRaf = requestAnimationFrame(step); else finish();
  };
  scrollRaf = requestAnimationFrame(step);
  // rAF can stall when heavy content composites mid-scroll; guarantee arrival regardless
  const backstop = setTimeout(finish, dur + 160);
  scrollToEl._cancel = () => { done = true; cancelAnimationFrame(scrollRaf); clearTimeout(backstop); };
}

function settle(root, target) {
  let n = 0;
  const fix = () => {
    if (++n > 6) return;
    if (Math.abs(target() - readY(root)) > 2) writeY(root, target());
    setTimeout(fix, 120);
  };
  setTimeout(fix, 80);
}

// cancel the animation if the visitor takes over
['wheel', 'touchstart', 'keydown'].forEach((ev) =>
  window.addEventListener(ev, () => { scrollToEl._cancel && scrollToEl._cancel(); }, { passive: true })
);

document.addEventListener('click', (e) => {
  const a = e.target.closest('a[href*="#"]');
  if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
  const href = a.getAttribute('href') || '';
  const hash = href.slice(href.indexOf('#'));
  if (hash.length < 2) return;
  // only handle links pointing at this page
  const path = href.split('#')[0];
  if (path && path !== window.location.pathname && !path.startsWith('#')) return;

  let el;
  try { el = document.querySelector(hash); } catch (_) { return; }
  if (!el) return;

  e.preventDefault();
  // close the mobile menu first so the measured header height is correct
  const hdr = headerEl();
  if (hdr && hdr.classList.contains('is-open')) {
    hdr.classList.remove('is-open');
    document.documentElement.classList.remove('w-scroll-lock');
    measureHeader();
  }
  scrollToEl(el);
  history.pushState(null, '', hash);
});

// deep link: honour the offset on load too
window.addEventListener('load', () => {
  if (window.location.hash.length > 1) {
    let el; try { el = document.querySelector(window.location.hash); } catch (_) {}
    if (el) setTimeout(() => scrollToEl(el, { instant: true }), 60);
  }
});


/* ---------- count-up for stat numbers ---------- */
function countUp(el) {
  const raw = el.dataset.value || el.textContent.trim();
  // split "  $4.2M+ " into prefix / number / suffix, keep everything except the number literal
  const m = raw.match(/^([^0-9]*)([0-9][0-9.,]*)(.*)$/);
  if (!m) return;
  const prefix = m[1], numStr = m[2], suffix = m[3];
  const decimals = (numStr.split('.')[1] || '').length;
  const useCommas = numStr.includes(',');
  const target = parseFloat(numStr.replace(/,/g, ''));
  if (!isFinite(target)) return;
  const dur = parseInt(el.dataset.dur, 10) || 1600;
  const fmt = (n) => {
    let s = n.toFixed(decimals);
    if (useCommas) { const [i, d] = s.split('.'); s = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (d ? '.' + d : ''); }
    return prefix + s + suffix;
  };
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { el.textContent = fmt(target); return; }
  const ease = (t) => 1 - Math.pow(1 - t, 4); // fast start, long soft landing
  const t0 = performance.now();
  el.textContent = fmt(0);
  const step = (now) => {
    const p = Math.min(1, (now - t0) / dur);
    el.textContent = fmt(target * ease(p));
    if (p < 1) requestAnimationFrame(step); else el.textContent = fmt(target);
  };
  requestAnimationFrame(step);
}
if ('IntersectionObserver' in window) {
  const cio = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      cio.unobserve(e.target);
      // small stagger so a row of numbers doesn't fire in perfect unison
      const i = [...e.target.closest('.w-grid')?.querySelectorAll('[data-count]') || []].indexOf(e.target);
      setTimeout(() => countUp(e.target), Math.max(0, i) * 90);
    });
  }, { threshold: 0.4 });
  const arm = () => document.querySelectorAll('[data-count]:not([data-count="off"]):not([data-armed])').forEach((el) => { el.setAttribute('data-armed', ''); cio.observe(el); });
  arm(); document.addEventListener('shopify:section:load', arm);
}

/* ---------- Base ---------- */
class WebbeElement extends HTMLElement {
  connectedCallback() { this._ac = new AbortController(); this.init(this._ac.signal); }
  disconnectedCallback() { this._ac?.abort(); }
  init() {}
  on(t, type, fn, o) { t.addEventListener(type, fn, { ...o, signal: this._ac.signal }); }
}
const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };

/* ---------- Header ---------- */
class WebbeHeader extends WebbeElement {
  init(signal) {
    console.info('[webbe] header js 3.3 · section', this.dataset.webbeVersion || 'unknown');
    this.toggle = this.querySelector('[data-w-menu-toggle]');
    this.drawer = this.querySelector('[data-w-drawer]');
    this.overlay = this.querySelector('[data-w-overlay]');
    this.nav = this.querySelector('[data-w-nav]');
    this.ind = this.nav?.querySelector('.w-nav__ind');
    this.links = this.nav ? [...this.nav.querySelectorAll('a')] : [];

    // Overlay header: publish own height so the first section can offset itself
    const publish = () => {
      document.documentElement.style.setProperty('--w-header-h', this.offsetHeight + 'px');
      document.documentElement.classList.toggle('w-header-overlay', this.hasAttribute('data-overlay'));
    };
    publish();
    if ('ResizeObserver' in window) { const ro = new ResizeObserver(publish); ro.observe(this); signal.addEventListener('abort', () => ro.disconnect()); }

    // Sentinel-based scroll detection: robust inside the theme editor and any custom scroll container
    let sentinel = document.getElementById('w-header-sentinel');
    if (!sentinel) { sentinel = document.createElement('div'); sentinel.id = 'w-header-sentinel'; sentinel.setAttribute('aria-hidden', 'true'); sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:24px;pointer-events:none;visibility:hidden;'; document.body.prepend(sentinel); }
    const scrollTop = () => Math.max(window.scrollY || 0, document.scrollingElement?.scrollTop || 0, document.documentElement.scrollTop || 0);
    const apply = (now) => {
      const was = this.hasAttribute('data-scrolled');
      this.toggleAttribute('data-scrolled', now);
      if (was !== now && this.active) {
        const t0 = performance.now(); const tick = () => { this.moveTo(this.hovering ? this.hoverEl : this.active, this.hovering, true); if (performance.now() - t0 < 700) requestAnimationFrame(tick); };
        requestAnimationFrame(tick);
      }
    };
    if ('IntersectionObserver' in window) {
      const sio = new IntersectionObserver((en) => apply(!en[0].isIntersecting), { threshold: 0 });
      sio.observe(sentinel); signal.addEventListener('abort', () => sio.disconnect());
    }
    const onScroll = () => { apply(scrollTop() > 24); };
    onScroll(); this.on(window, 'scroll', onScroll, { passive: true });

    // Sliding indicator: follows hover, rests on the active link
    if (this.nav && this.ind) {
      this.links.forEach((a) => {
        this.on(a, 'mouseenter', () => this.moveTo(a, true));
        this.on(a, 'focus', () => this.moveTo(a, true));
      });
      this.on(this.nav, 'mouseleave', () => this.moveTo(this.active, false));
      this.on(this.nav, 'focusout', (e) => { if (!this.nav.contains(e.relatedTarget)) this.moveTo(this.active, false); });
      this.on(window, 'resize', () => this.moveTo(this.active, false, true));
      // pre-mark a link whose href matches the current path
      const path = location.pathname.replace(/\/$/, '') || '/';
      const cur = this.links.find((a) => { try { const u = new URL(a.href); return !u.hash && (u.pathname.replace(/\/$/, '') || '/') === path; } catch (_) { return false; } });
      if (cur) this.setActive(cur, true);
    }

    // Same-page anchor tracking
    const anchors = this.links.filter((a) => a.getAttribute('href')?.includes('#'));
    if (anchors.length && 'IntersectionObserver' in window) {
      const map = new Map();
      anchors.forEach((a) => { const h = a.getAttribute('href'); const id = h.slice(h.indexOf('#')); try { const t = document.querySelector(id); if (t) map.set(t, a); } catch (_) {} });
      if (map.size) {
        // Which section "owns" the viewport: the one crossing a band just above centre.
        const io = new IntersectionObserver((entries) => { for (const e of entries) if (e.isIntersecting) this.setActive(map.get(e.target)); }, { rootMargin: '-40% 0px -55% 0px' });
        map.forEach((_, t) => io.observe(t)); signal.addEventListener('abort', () => io.disconnect());

        // Hero release: the indicator disappears while the hero is the dominant section.
        // Independent of window.scrollY, so it works with inner scroll containers and the theme editor.
        const first = [...map.keys()][0];
        const hero = document.querySelector('main .w-section, main section') !== first ? document.querySelector('main .w-section, main section') : null;
        if (hero) {
          const pct = Math.max(10, Math.min(90, parseInt(this.dataset.heroRelease || '40', 10))) / 100;
          const hio = new IntersectionObserver((en) => {
            const e = en[0];
            if (e.isIntersecting && e.intersectionRatio >= pct) this.setActive(null);
          }, { threshold: [0, pct, Math.min(1, pct + 0.01)] });
          hio.observe(hero); signal.addEventListener('abort', () => hio.disconnect());
        }
      }
    }

    // Optional mobile drawer
    if (this.toggle && this.drawer) {
      this.on(this.toggle, 'click', (e) => { e.preventDefault(); this.setOpen(!this.isOpen); });
      this.on(document, 'keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.setOpen(false); });
      if (this.overlay) this.on(this.overlay, 'click', () => this.setOpen(false));
      this.drawer.querySelectorAll('a').forEach((a) => this.on(a, 'click', () => this.setOpen(false)));
      this.on(window, 'resize', () => { if (window.innerWidth >= 990 && this.isOpen) this.setOpen(false); });
    }
  }
  setActive(a, instant) {
    this.active = a || null;
    this.links.forEach((l) => l.classList.toggle('is-active', l === a));
    if (!this.hovering) this.moveTo(a, false, instant);
  }
  moveTo(a, hover, instant) {
    if (!this.ind) return;
    this.hovering = !!hover; if (hover) this.hoverEl = a;
    this.links.forEach((l) => { l.classList.toggle('is-hover', hover && l === a); l.classList.toggle('is-under', l === a); });
    if (!a) { this.ind.classList.remove('is-on'); return; }
    const r = a.getBoundingClientRect(), n = this.nav.getBoundingClientRect();
    const cs = getComputedStyle(this.nav);
    const inset = (parseFloat(cs.borderLeftWidth) || 0);
    if (instant) this.ind.style.transition = 'none';
    this.ind.style.transform = `translateX(${r.left - n.left - inset}px)`;
    this.ind.style.width = r.width + 'px';
    this.ind.classList.add('is-on');
    if (instant) requestAnimationFrame(() => { this.ind.style.transition = ''; });
  }
  get isOpen() { return this.hasAttribute('data-open'); }
  setOpen(v) {
    this.toggleAttribute('data-open', v); this.toggle?.setAttribute('aria-expanded', String(v));
    document.documentElement.classList.toggle('w-scroll-lock', v);
    if (v) { const f = this.drawer.querySelector('a, button'); f && setTimeout(() => f.focus(), 60); } else this.toggle?.focus();
  }
}
define('webbe-header', WebbeHeader);

/* ---------- Tabs (process) ---------- */
class WebbeTabs extends WebbeElement {
  init() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')]; this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((t, i) => {
      this.on(t, 'click', () => this.select(i));
      this.on(t, 'keydown', (e) => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length; this.select(n); this.tabs[n].focus(); } });
    });
    const start = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')); this.select(start, true);
  }
  select(i, instant) {
    this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', String(k === i)); t.tabIndex = k === i ? 0 : -1; });
    this.panels.forEach((p, k) => {
      const on = k === i;
      if (on) { p.hidden = false; requestAnimationFrame(() => p.classList.add('is-on')); } else { p.classList.remove('is-on'); if (instant) p.hidden = true; else setTimeout(() => { if (!p.classList.contains('is-on')) p.hidden = true; }, 220); }
    });
  }
}
define('webbe-tabs', WebbeTabs);

/* ---------- Filter (portfolio) ---------- */
class WebbeFilter extends WebbeElement {
  init() {
    this.btns = [...this.querySelectorAll('[data-filter]')]; this.items = [...this.querySelectorAll('[data-cat]')];
    this.btns.forEach((b) => this.on(b, 'click', () => this.apply(b.dataset.filter)));
    const first = this.btns.find((b) => b.getAttribute('aria-pressed') === 'true') || this.btns[0]; if (first) this.apply(first.dataset.filter, true);
  }
  apply(f, instant) {
    this.btns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.filter === f)));
    this.items.forEach((it, i) => {
      const show = f === 'all' || it.dataset.cat === f;
      if (show) { it.hidden = false; it.style.setProperty('--i', String(i % 12)); requestAnimationFrame(() => it.classList.add('is-in')); }
      else { it.classList.remove('is-in'); if (instant) it.hidden = true; else setTimeout(() => { if (!it.classList.contains('is-in')) it.hidden = true; }, 260); }
    });
  }
}
define('webbe-filter', WebbeFilter);

/* ---------- Video tiles (testimonials) ---------- */
class WebbeVideo extends WebbeElement {
  init() {
    this.v = this.querySelector('video'); if (!this.v) return;
    this.on(this, 'click', (e) => { if (e.target.closest('a')) return; this.v.paused ? this.play() : this.pause(); });
    this.on(this.v, 'ended', () => this.pause());
    this.on(document, 'webbe:video:play', (e) => { if (e.detail !== this) this.pause(); });
  }
  play() { document.dispatchEvent(new CustomEvent('webbe:video:play', { detail: this })); this.v.muted = false; this.v.play().catch(() => {}); this.setAttribute('data-playing', ''); }
  pause() { this.v.pause(); this.removeAttribute('data-playing'); }
}
define('webbe-video', WebbeVideo);

/* ---------- Process rows: scroll-linked focus ---------- */
class WebbeScroll extends WebbeElement {
  init(signal) {
    this.steps = [...this.querySelectorAll('[data-step]')];
    if (!this.steps.length) return;
    this.initFocus(signal);
  }
  initFocus(signal) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const min = (parseFloat(this.dataset.focusMin) || 0) / 100;
    const point = (parseFloat(this.dataset.focusPoint) || 42) / 100;
    const band = (parseFloat(this.dataset.focusBand) || 22) / 100;
    const fade = (parseFloat(this.dataset.focusFade) || 30) / 100;
    let raf = 0;
    const paint = () => {
      raf = 0;
      const vh = window.innerHeight, centre = vh * point, half = vh * band / 2, fadePx = vh * fade;
      this.steps.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) { el.style.setProperty('--focus', min); return; }
        // distance from the step's centre to the reading zone edge, 0 inside the zone
        const c = r.top + r.height / 2;
        const d = Math.max(0, Math.abs(c - centre) - half);
        const t = Math.min(1, d / fadePx);
        const eased = 1 - t * t; // linear near the zone, gentle at the far end
        el.style.setProperty('--focus', (min + (1 - min) * eased).toFixed(3));
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(paint); };
    this.on(window, 'scroll', onScroll, { passive: true });
    const root = (typeof scrollRoot === 'function') ? scrollRoot(this) : null;
    if (root && root !== document.scrollingElement && root !== document.documentElement) this.on(root, 'scroll', onScroll, { passive: true });
    this.on(window, 'resize', onScroll);
    paint();
    signal.addEventListener('abort', () => cancelAnimationFrame(raf));
  }
}
define('webbe-scroll', WebbeScroll);

/* ---------- Portfolio (filter + slider + hover auto-scroll) ---------- */
class WebbePortfolio extends WebbeElement {
  init() {
    this.track = this.querySelector('[data-track]'); if (!this.track) return;
    this.all = [...this.track.querySelectorAll('[data-slide]')];
    this.tabs = [...this.querySelectorAll('[data-filter]')];
    this.prev = this.querySelector('[data-prev]'); this.next = this.querySelector('[data-next]'); this.fill = this.querySelector('[data-fill]');
    this.pps = parseFloat(this.dataset.scrollDur) || 140; // pixels per second

    this.tabs.forEach((b) => this.on(b, 'click', () => this.filter(b.dataset.filter)));
    if (this.prev) this.on(this.prev, 'click', () => this.go(-1));
    if (this.next) this.on(this.next, 'click', () => this.go(1));
    let raf = 0; this.on(this.track, 'scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; this.update(); }); }, { passive: true });
    this.on(window, 'resize', () => { this.measure(); this.update(); });
    this.on(this.track, 'keydown', (e) => { if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); } if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); } });

    // measure once images decode
    this.querySelectorAll('[data-img] img').forEach((img) => {
      if (img.complete) this.measureOne(img); else this.on(img, 'load', () => this.measureOne(img));
    });
    if ('ResizeObserver' in window) { this._ro = new ResizeObserver(() => this.measure()); this.querySelectorAll('[data-img]').forEach((w) => this._ro.observe(w)); }


    // lightbox for flat cards
    this.lb = this.querySelector('[data-lb]');
    if (this.lb) {
      // Ancestors with transform/will-change trap position:fixed, so host the overlay on <body>.
      document.body.appendChild(this.lb);
      this.lbImg = this.lb.querySelector('[data-lb-img]'); this.lbCap = this.lb.querySelector('[data-lb-cap]');
      const touch = window.matchMedia('(hover: none)');
      this.querySelectorAll('[data-zoom]').forEach((btn) => this.on(btn, 'click', (e) => { e.preventDefault(); e.stopPropagation(); this.openLb(btn.closest('[data-img]')); }));
      this.querySelectorAll('[data-img][data-src]').forEach((w) => this.on(w, 'click', (e) => {
        if (e.target.closest('a, [data-zoom]')) return;
        // flat cards always open; tall cards only on touch, where hover-scroll can't run
        if (!w.closest('.pf').classList.contains('flat') && !touch.matches) return;
        this.openLb(w);
      }));
      this.lb.querySelectorAll('[data-lb-close]').forEach((el) => this.on(el, 'click', (e) => { if (e.target === el) this.closeLb(); }));
      this.on(this.lb.querySelector('[data-lb-x]'), 'click', () => this.closeLb());
      this.on(document, 'keydown', (e) => { if (e.key === 'Escape' && this.lb.hasAttribute('open')) this.closeLb(); });
    }

    const start = this.tabs.find((b) => b.getAttribute('aria-pressed') === 'true') || this.tabs[0];
    this.filter(start ? start.dataset.filter : 'all', true);
  }
  disconnectedCallback() { this._ro && this._ro.disconnect(); document.documentElement.classList.remove('w-lb-open'); this.lb && this.lb.remove(); super.disconnectedCallback(); }
  openLb(wrap) {
    if (!wrap || !wrap.dataset.src) return;
    const esc = (v) => (v || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const link = wrap.dataset.link, name = wrap.dataset.caption || '', sub = wrap.dataset.sub || '';
    this.lbImg.src = wrap.dataset.src; this.lbImg.alt = name;
    let html = '';
    if (name) html += '<b>' + esc(name) + '</b>';
    if (sub) html += '<span>' + esc(sub) + '</span>';
    if (this.dataset.lbLink === 'true' && link) html += '<a href="' + esc(link) + '" target="_blank" rel="noopener">' + esc(this.dataset.lbLinkText || 'Open live page') + '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg></a>';
    this.lbCap.innerHTML = html; this.lbCap.hidden = !html;
    this._opener = wrap.querySelector('[data-zoom]') || wrap;
    this.lb.hidden = false; this.lb.setAttribute('open', '');
    document.documentElement.classList.add('w-lb-open');
    this.lb.querySelector('[data-lb-scroll], .w-lb__scroll').scrollTop = 0;
    requestAnimationFrame(() => { this.lb.classList.add('is-in'); this.lb.querySelector('[data-lb-x]').focus(); });
  }
  closeLb() {
    this.lb.classList.remove('is-in');
    document.documentElement.classList.remove('w-lb-open');
    setTimeout(() => { this.lb.removeAttribute('open'); this.lb.hidden = true; this.lbImg.src = ''; }, 320);
    this._opener && this._opener.focus();
  }

  measure() { this.querySelectorAll('[data-img] img').forEach((img) => this.measureOne(img)); }
  measureOne(img) {
    const wrap = img.closest('[data-img]'); if (!wrap || !img.naturalHeight) return;
    if (wrap.closest('.pf.flat')) { wrap.classList.remove('is-tall'); return; }
    const h = wrap.clientWidth * (img.naturalHeight / img.naturalWidth); // rendered height at 100% width
    const dist = Math.max(0, Math.round(h - wrap.clientHeight));
    if (dist > 24) {
      wrap.classList.add('is-tall');
      wrap.style.setProperty('--scroll', dist + 'px');
      wrap.style.setProperty('--scroll-dur', Math.min(14, Math.max(1.2, dist / this.pps)).toFixed(2) + 's');
      wrap.style.setProperty('--scroll-out', Math.min(1.2, Math.max(.45, dist / (this.pps * 4))).toFixed(2) + 's');
    } else { wrap.classList.remove('is-tall'); wrap.style.removeProperty('--scroll'); }
  }
  filter(f, instant) {
    this.tabs.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.filter === f)));
    const show = (el) => f === 'all' || el.dataset.cat === f;
    if (instant) { this.all.forEach((el) => { el.hidden = !show(el); el.removeAttribute('data-out'); }); this.track.scrollLeft = 0; this.measure(); this.update(); return; }
    this.all.forEach((el) => { if (!el.hidden) el.setAttribute('data-out', ''); });
    clearTimeout(this._t);
    this._t = setTimeout(() => {
      this.all.forEach((el) => { el.hidden = !show(el); });
      this.track.scrollLeft = 0; this.measure();
      requestAnimationFrame(() => { this.all.forEach((el) => el.removeAttribute('data-out')); this.update(); });
    }, 260);
  }
  shown() { return this.all.filter((el) => !el.hidden); }
  step() { const v = this.shown(); if (v.length < 2) return this.track.clientWidth; return v[1].offsetLeft - v[0].offsetLeft; }
  visible() { return Math.max(1, Math.round(this.track.clientWidth / this.step())); }
  go(d) { const n = this.shown().length, i = Math.round(this.track.scrollLeft / this.step()); const t = Math.max(0, Math.min(n - this.visible(), i + d)); this.track.scrollTo({ left: t * this.step(), behavior: 'smooth' }); }
  update() {
    const max = this.track.scrollWidth - this.track.clientWidth, x = this.track.scrollLeft;
    const n = this.shown().length, v = this.visible(), enough = n > v;
    if (this.prev) this.prev.disabled = !enough || x <= 1;
    if (this.next) this.next.disabled = !enough || x >= max - 1;
    if (this.fill) { const w = enough ? Math.min(100, (v / n) * 100) : 100; const p = max > 0 ? x / max : 0; this.fill.style.width = w + '%'; this.fill.style.transform = 'translateX(' + (w < 100 ? p * (100 - w) / w * 100 : 0) + '%)'; }
  }
}
define('webbe-portfolio', WebbePortfolio);

/* ---------- Slider (results) ---------- */
class WebbeSlider extends WebbeElement {
  init() {
    this.track = this.querySelector('[data-track]'); if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-slide]')];
    this.prev = this.querySelector('[data-prev]'); this.next = this.querySelector('[data-next]');
    this.fill = this.querySelector('[data-fill]'); this.count = this.querySelector('[data-count]');
    this.on(this.prev, 'click', () => this.go(-1)); this.on(this.next, 'click', () => this.go(1));
    let raf = 0; this.on(this.track, 'scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; this.update(); }); }, { passive: true });
    this.on(window, 'resize', () => this.update());
    this.on(this.track, 'keydown', (e) => { if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); } if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); } });
    this.update();
  }
  step() { const a = this.slides[0], b = this.slides[1]; return b ? b.offsetLeft - a.offsetLeft : a.offsetWidth; }
  index() { return Math.round(this.track.scrollLeft / this.step()); }
  visible() { return Math.max(1, Math.round(this.track.clientWidth / this.step())); }
  go(d) { const i = Math.max(0, Math.min(this.slides.length - this.visible(), this.index() + d)); this.track.scrollTo({ left: i * this.step(), behavior: 'smooth' }); }
  update() {
    const max = this.track.scrollWidth - this.track.clientWidth, x = this.track.scrollLeft, n = this.slides.length, v = this.visible();
    if (this.prev) this.prev.disabled = x <= 1; if (this.next) this.next.disabled = x >= max - 1;
    if (this.fill) { const w = Math.min(100, (v / n) * 100); const p = max > 0 ? x / max : 0; this.fill.style.width = w + '%'; this.fill.style.transform = 'translateX(' + (p * (100 - w) / w * 100) + '%)'; }
    if (this.count) this.count.textContent = (Math.min(n, this.index() + 1)) + ' / ' + n;
  }
}
define('webbe-slider', WebbeSlider);

/* ---------- ROI calculator ---------- */
class WebbeRoi extends WebbeElement {
  init() {
    const q = (s) => this.querySelector(s);
    this.rv = q('[data-visitors]'); this.ra = q('[data-aov]'); this.rc = q('[data-cvr]'); this.rl = q('[data-lift]'); this.rp = q('[data-pts]');
    this.out = { rv: q('[data-o-visitors]'), ra: q('[data-o-aov]'), rc: q('[data-o-cvr]'), rl: q('[data-o-lift]'), rp: q('[data-o-pts]'), hint: q('[data-o-hint]'), cur: q('[data-o-current]'), nw: q('[data-o-newcvr]'), m: q('[data-o-month]'), y: q('[data-o-year]') };
    this.cur = this.dataset.currency || '$';
    this.mode = this.dataset.mode === 'pts' ? 'pts' : 'pct';
    this.flds = { pct: q('[data-fld="pct"]'), pts: q('[data-fld="pts"]') };
    this.modeBtns = [...this.querySelectorAll('[data-mode-btn]')]; this.modeInd = q('[data-mode-ind]');
    this.modeBtns.forEach((b) => this.on(b, 'click', () => this.setMode(b.dataset.modeBtn)));
    this.on(window, 'resize', () => this.moveInd(true));
    [this.rv, this.ra, this.rc, this.rl, this.rp].forEach((el) => el && this.on(el, 'input', () => this.calc()));
    this.setMode(this.mode, true);
  }
  setMode(mode, instant) {
    this.mode = mode;
    Object.entries(this.flds).forEach(([k, el]) => { if (el) el.hidden = k !== mode; });
    this.modeBtns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.modeBtn === mode)));
    this.moveInd(instant);
    this.calc();
  }
  moveInd(instant) {
    if (!this.modeInd) return;
    const a = this.modeBtns.find((b) => b.dataset.modeBtn === this.mode); if (!a) return;
    const wrap = a.parentElement.getBoundingClientRect(), r = a.getBoundingClientRect();
    if (instant) this.modeInd.style.transition = 'none';
    this.modeInd.style.transform = 'translateX(' + (r.left - wrap.left - 1) + 'px)'; this.modeInd.style.width = r.width + 'px';
    if (instant) requestAnimationFrame(() => { this.modeInd.style.transition = ''; });
  }
  fmt(n) { return this.cur + Math.round(n).toLocaleString('en-US'); }
  pct(el) { if (!el) return; const min = +el.min, max = +el.max, v = +el.value; el.style.setProperty('--p', ((v - min) / (max - min) * 100) + '%'); }
  trim(n, d) { return n.toFixed(d).replace(/\.?0+$/, ''); }
  calc() {
    const v = +this.rv.value, a = +this.ra.value, c = +this.rc.value;
    let nc;
    if (this.mode === 'pts' && this.rp) {
      const p = +this.rp.value; nc = c + p;
      this.out.rp.textContent = '+' + this.trim(p, 1) + ' pts';
      if (this.out.hint) this.out.hint.textContent = 'That\u2019s a ' + Math.round((p / c) * 100) + '% relative lift on ' + this.trim(c, 1) + '%.';
    } else {
      const l = +this.rl.value; nc = c * (1 + l / 100);
      this.out.rl.textContent = l + '%';
    }
    const cur = v * (c / 100) * a, extra = v * (nc / 100) * a - cur;
    this.out.rv.textContent = v.toLocaleString('en-US'); this.out.ra.textContent = this.cur + a; this.out.rc.textContent = this.trim(c, 1) + '%';
    this.out.cur.textContent = this.fmt(cur); this.out.nw.textContent = this.trim(nc, 2) + '%';
    this.out.m.textContent = this.fmt(extra); this.out.y.textContent = this.fmt(extra * 12);
    [this.rv, this.ra, this.rc, this.rl, this.rp].forEach((el) => this.pct(el));
  }
}

define('webbe-roi', WebbeRoi);

export { WebbeElement, bindReveals };